import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import query from 'query-string'
import { JSON_TYPE } from '../utils/client-server/mime-types'
import { DELETE, GET, PATCH, POST, PUT } from '../utils/client-server/method-types'

export const commonRequestMiddlewares = []
export const commonResponseMiddlewares = []
export const commonErrorMiddlewares = []

let counter = 1

export const getRequestId = () => {
    return counter++
}

export class BaseService {
    serverAddress = ''

    defaultConfig = {
        headers: {
            'Cache-Control': 'no-store, must-revalidate'
        },
        mode: 'cors',
        credentials: 'omit',
    }

    /**
     * @param {string} url
     * @param {object} config
     * @param {string} config.method - метод запроса
     * @param {object} config.headers - заголовки запроса
     * @param {*} config.body - тело запроса
     * @param {'same-origin'|'no-cors'|'cors'} config.mode - режим кросс-доменности
     * @param {'omit'|'same-origin'|'include'} config.credentials - пересылка куки и заголовков авторизации вместе с запросом
     * @param {'default'|'no-store'|'reload'|'no-cache'|'force-cache'|'only-if-cached'} config.cache - кеширование запроса
     * @param {'follow'|'error'} - follow: следовать редиректу, error: интерпретация редиректа как ошибки
     * @param {object} options
     * @param {function(object) => void} options.errorMiddlewares
     * @param {function(object) => void} options.requestMiddlewares
     * @param {function(object) => void} options.responseMiddlewares
     * @returns {Promise}
     */
    async send(url = '', config = {}, options = {}) {
        if (!url) return Promise.resolve()

        const { errorMiddlewares, requestMiddlewares, responseMiddlewares } = options
        const { body, method = GET } = config
        const id = getRequestId()

        if (commonRequestMiddlewares.length > 0) {
            // Перед запросом прогоним конфиг через все requestMiddleware
            commonRequestMiddlewares.forEach(middleware => middleware(config))
        }
        if (requestMiddlewares) {
            requestMiddlewares.forEach(middleware => middleware(config))
        }

        const queryParams = query.extract(url)

        console.info(
            `==> ${query.parseUrl(url).url}, details:\n`,
            omitBy(
                {
                    id,
                    method: method.toUpperCase(),
                    queryParams: queryParams ? query.parse(queryParams) : null,
                    payload: body
                },
                isNil
            )
        )
        const requestStart = Date.now()

        try {
            const response = await fetch(url, { ...config, method })

            if (commonResponseMiddlewares.length > 0) {
                // Перед возвратом response прогоняем его через все responseMiddleware
                commonResponseMiddlewares.forEach(middleware => middleware(response))
            }

            if (responseMiddlewares) {
                responseMiddlewares.forEach(middleware => middleware(response))
            }

            let payload = null

            const contentTypeHeader = response.headers.get('content-type')

            if (contentTypeHeader && contentTypeHeader.indexOf(JSON_TYPE) !== -1) {
                try {
                    payload = await response.json()
                } catch (error) {
                    console.error(error.message)
                }
            }

            const responseQueryParams = query.extract(response.url)

            console.info(
                `<== ${query.parseUrl(response.url).url}, ${Date.now() - requestStart}ms, details:\n`,
                omitBy(
                    {
                        id,
                        queryParams: responseQueryParams ? query.parse(responseQueryParams) : null,
                        payload
                    },
                    isNil
                )
            )

            return payload

        } catch (error) {
            console.error(`<== ${Date.now() - requestStart}ms, error:\n`, error)

            if (commonErrorMiddlewares.length > 0) {
                // Перед возвратом ошибки прогоняем ее через все errorMiddleware
                commonErrorMiddlewares.forEach(middleware => middleware(error))
            }
            if (errorMiddlewares) {
                errorMiddlewares.forEach(middleware => middleware(error))
            }
        }
    }

    /**
     * Обёртка над send, позволяет явно указать часто используемые параметры
     * @param {string} method - http-метод
     * @param {string} relativeURL - относительный url запроса
     * @param {object} queryParams - объект без nested свойств, сериализуется с помощью stringify из query-string
     * @param {object} payload - body запроса, сериализуется с помощью JSON.stringify
     * @param {object} config - переопределение config'а запроса
     * @param {object} options
     * @param {function(object) => void} options.errorMiddlewares
     * @param {function(object) => void} options.requestMiddlewares
     * @param {function(object) => void} options.responseMiddlewares
     */
    async sendSimplified(method, relativeURL, queryParams, payload, config = {}, options = {}) {
        const queryString = !isNil(queryParams) ? query.stringify(queryParams) : ''

        let url = relativeURL
        if (!url.startsWith('/')) url = `/${url}`

        if (queryString) {
            if (url.endsWith('/') || url.endsWith('?')) url = url.substr(0, url.length - 1)
            if (url.includes('?')) {
                if (!url.endsWith('&')) url += '&'
            } else {
                url += '?'
            }
            url += queryString
        }
        url = `${this.serverAddress}${url}`

        if (url.endsWith('/')) url = url.substr(0, url.length - 1)

        return this.send(
            url,
            {
                ...this.defaultConfig,
                method,
                body: payload ? JSON.stringify(payload) : undefined,
                ...config
            },
            options
        )
    }

    async delete(url, queryParams, config = {}, options = {}) {
        return this.sendSimplified(DELETE, url, queryParams, undefined, config, options)
    }

    async get(url, queryParams, config = {}, options = {}) {
        return this.sendSimplified(GET, url, queryParams, undefined, config, options)
    }

    async patch(url, queryParams, payload, config = {}, options = {}) {
        return this.sendSimplified(PATCH, url, queryParams, payload, config, options)
    }

    async post(url, queryParams, payload, config = {}, options = {}) {
        return this.sendSimplified(POST, url, queryParams, payload, config, options)
    }

    async put(url, queryParams, payload, config = {}, options = {}) {
        return this.sendSimplified(PUT, url, queryParams, payload, config, options)
    }
}
